import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faInfoCircle, faCheckCircle, faExclamationTriangle, faLightbulb, faShieldAlt, faUsers, faSmile, faMeh, faFrown, faBuilding } from "@fortawesome/free-solid-svg-icons";
import TradingRangeInsights from './TradingRangeInsights';
import WebTrafficSection from './WebTrafficSection';
import ExpectationsBarMeter from './ExpectationsBarMeter';
import AnalystRatingText from './AnalystRatingText';
import CompetitionSection from './CompetitionSection';
import { AiAssessment, AiCallText, DevelopmentsAndRisks, SectionHeading } from './AiAnalysisSections';
import { Tooltip } from 'react-tooltip';
import { fetchGlassdoorSummary } from '../services/api';
import { formatMarketCap, formatSignedPercent } from './utils';
import { lastReportSummary, sessionsLabel } from '../utils/earningsElapsed';
import { analysisAsOf, aiRecommendation, aiSpeculation } from '../utils/aiAnalysisDisplay';
import { columnByKey, columnTooltip } from '../config/columnConfig';
import { METRIC_DESCRIPTIONS } from '../constants/metricDescriptions';
import { TEXT_SUBTLE, TEXT_BODY, signColor } from '../constants/uiColors';
import './AiAnalysisPanel.css';


const METRIC_THRESHOLDS = {
  // Growth & Valuation
  revenueGrowth: {
    type: 'above',
    good: 15,  // >15% is good
    warning: 5, // 5-15% is warning
    // <5% is bad
  },
  pegRatio: {
    type: 'range',
    good: { min: 0.5, max: 1.5 },
    warning: { min: 0.25, max: 2.0 },
    // Outside range is bad
  },
  priceToSales: {
    type: 'below',
    good: 3,    // <3 is good
    warning: 5, // 3-5 is warning
    // >5 is bad
  },
  enterpriseToRevenue: {
    type: 'below',
    good: 3,
    warning: 5,
  },
  enterpriseToEbitda: {
    type: 'range',
    good: { min: 8, max: 15 },
    warning: { min: 5, max: 20 },
  },

  // Profitability & Margins  
  grossMargin: {
    type: 'above',
    good: 30,
    warning: 20,
  },
  operatingMargin: {
    type: 'above', 
    good: 15,
    warning: 10,
  },
  trailingEps: {
    type: 'above',
    good: 0,  // Profitable
  },
  forwardEps: {
    type: 'above',
    good: 0,  // Expected profitable
  },

  // Liquidity & Risk
  quickRatio: {
    type: 'range',
    good: { min: 1, max: 3 },
    warning: { min: 0.8, max: 4 },
  },
  currentRatio: {
    type: 'range',
    good: { min: 1.5, max: 3 },
    warning: { min: 1.2, max: 4 },
  },
  cashPerShare: {
    type: 'above',
    good: 1,
    warning: 0.5,
  },
  shortPercentOfFloat: {
    type: 'below',
    good: 5,     // <5% is good
    warning: 10, // 5-10% is warning
    // >10% is concerning
  },
  beta: {
    type: 'range',
    good: { min: 0.8, max: 1.2 },
    warning: { min: 0.5, max: 1.5 },
  },
  trailingPE: {
    type: 'range',
    good: { min: 10, max: 25 },
    warning: { min: 5, max: 40 },
  },
  forwardPE: {
    type: 'range',
    good: { min: 10, max: 25 },
    warning: { min: 5, max: 40 },
  },
  priceToBook: {
    type: 'below',
    good: 3,
    warning: 5,
  },
  dividendYield: {
    type: 'above',
    good: 2,
    warning: 1,
  },
  alpha12m: {
    type: 'above',
    good: 5,
    warning: 0,
  }
};

// The table column's own explanation and formatter, so a profile tile showing
// the same field can't drift from the column.
const columnFormat = (key, value) => columnByKey(key)?.format?.(value) ?? null;

// Inline text styles shared across this panel's sections.
const QUIET_STYLE = { fontWeight: 400, color: TEXT_SUBTLE };
const NOTE_STYLE = { color: TEXT_SUBTLE, fontStyle: 'italic' };

// One Company Snapshot tile. Inline background/padding: `bg-gray-50` and `p-3`
// are absent from the prebuilt tailwind-output.css, so the tiles this replaced
// never actually got either.
const TILE_STYLE = { background: '#f9fafb', borderRadius: 8, padding: 12 };

const SnapshotTile = ({ label, title, children }) => (
  <div style={TILE_STYLE} title={title}>
    <div style={{ color: TEXT_SUBTLE, fontSize: '0.75rem', marginBottom: 4 }}>{label}</div>
    <div className="font-semibold" style={{ fontSize: '1rem' }}>{children}</div>
  </div>
);

// One "Label: value" pair in the Employee Insights metrics row.
const EmployeeMetric = ({ label, title, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} title={title}>
    <span style={{ color: TEXT_SUBTLE }}>{label}:</span>
    <span className="font-semibold">{children}</span>
  </div>
);

const SnapshotSub = ({ children }) => (
  <div style={{ fontSize: '0.8rem', fontWeight: 400, color: TEXT_BODY, marginTop: 2 }}>{children}</div>
);

const getMetricColor = (value, metricName) => {
  const threshold = METRIC_THRESHOLDS[metricName];
  if (!threshold || isNaN(value)) return '';

  const numValue = parseFloat(value);

  switch (threshold.type) {
    case 'above':
      if (numValue >= threshold.good) return 'positive';
      if (numValue >= threshold.warning) return 'warning';
      return 'negative';

    case 'below':
      if (numValue <= threshold.good) return 'positive';
      if (numValue <= threshold.warning) return 'warning';
      return 'negative';

    case 'range':
      if (numValue >= threshold.good.min && numValue <= threshold.good.max) return 'positive';
      if (numValue >= threshold.warning.min && numValue <= threshold.warning.max) return 'warning';
      return 'negative';

    default:
      return '';
  }
};

const MetricRow = ({ 
  label, 
  value, 
  tooltip, 
  isPercentage, 
  isCurrency,
  metricName,  // Add this prop
  analysis 
}) => {
  const id = `metric-${label.toLowerCase().replace(/\s+/g, '-')}`;
  
  let formattedValue = value;
  if (value === 'N/A') {
    formattedValue = 'N/A';
  } else if (isPercentage) {
    formattedValue = `${value}%`;
  } else if (isCurrency) {
    formattedValue = `$${value}`;
  }

  const colorClass = getMetricColor(value, metricName);
  const valueClass = `metric-value ${colorClass}`;

  return (
    <div className="metric-row">
      <div className="metric-label">
        {label}
        <FontAwesomeIcon 
          icon={faInfoCircle} 
          className="info-icon"
          data-tooltip-id={id}
        />
        <Tooltip 
          id={id}
          content={
            <>
              {tooltip}
              {analysis && (
                <>
                  <br/><br/>
                  <span className="font-medium">Analysis:</span> {analysis}
                </>
              )}
            </>
          }
          className="metric-tooltip"
        />
      </div>
      <div className={valueClass}>
        {formattedValue}
      </div>
    </div>
  );
};

const AiAnalysisPanel = ({ aiAnalysis, stock }) => {
  // Glassdoor AI summary, fetched on demand when the row didn't carry one.
  //
  // ONE attempt per symbol, recorded when the request STARTS. The previous
  // version marked a symbol done only once a summary came back, and listed
  // `loading` among its effect deps — so any answer without a summary (404 for
  // no reviews, 401 when logged out, an empty result) flipped loading back to
  // false, re-ran the effect and fetched again: 241 requests in a few seconds
  // for PMAX, plus a "Maximum update depth exceeded" render storm (issue #445).
  const symbol = stock?.tickerSymbol;
  const hasGlassdoor = !!(stock?.glassdoorReviewCount || stock?.hasGlassdoorMatch);
  const wantsSummaryFetch = !!symbol && !stock?.glassdoorAiSummary && hasGlassdoor;
  const attemptedSymbolRef = useRef(null);
  const [fetchedSummary, setFetchedSummary] = useState({ symbol: null, summary: null, sentiment: null, loading: false });

  useEffect(() => {
    if (!wantsSummaryFetch || attemptedSymbolRef.current === symbol) return;
    attemptedSymbolRef.current = symbol;
    setFetchedSummary({ symbol, summary: null, sentiment: null, loading: true });
    fetchGlassdoorSummary(symbol)
      .then((result) => ({ summary: result?.ai_summary ?? null, sentiment: result?.ai_sentiment ?? null }))
      .catch((error) => {
        console.error('[AI_PANEL] Glassdoor summary fetch failed:', symbol, error.message);
        return { summary: null, sentiment: null };
      })
      // Keyed by symbol: a late answer for a stock no longer shown is dropped.
      .then((found) => setFetchedSummary((prev) => (prev.symbol === symbol ? { symbol, ...found, loading: false } : prev)));
  }, [symbol, wantsSummaryFetch]);

  // The row's own summary wins; a fetched one counts only for the stock it was
  // fetched for, so navigating between tickers never shows the previous one's.
  const fetched = fetchedSummary.symbol === symbol ? fetchedSummary : null;
  const glassdoorData = stock?.glassdoorAiSummary
    ? { summary: stock.glassdoorAiSummary, sentiment: stock.glassdoorAiSentiment ?? null, loading: false }
    : { summary: fetched?.summary ?? null, sentiment: fetched?.sentiment ?? null, loading: !!fetched?.loading };

  if (!stock) {
    return <div>No stock data available.</div>;
  }

  // The stock's own data (trading range, snapshot, metrics, employees, traffic)
  // renders with or without an AI analysis; only the AI sections need one.
  // An empty object keeps every `analysis_data.x` read below a safe miss.
  const hasAnalysis = !!aiAnalysis?.analysis_data;
  const analysis_data = aiAnalysis?.analysis_data || {};
  const asOf = hasAnalysis ? analysisAsOf(aiAnalysis.updated_at) : null;
  // The AI's call for the snapshot tile — the same parse and colour ladder as
  // the AI Assessment card below and the Analyst Consensus tile beside it.
  const aiCall = hasAnalysis ? aiRecommendation(aiAnalysis.recommendation_rating) : null;
  const speculation = hasAnalysis ? aiSpeculation(aiAnalysis.summary_rating) : null;

  const lastReport = lastReportSummary({
    reactionDate: stock.earningsReactionDate,
    reactionPercent: stock.earningsReactionPercent,
    sincePercent: stock.stockChange,
  });
  const hasEmployeeMetrics = stock.outlook != null || stock.positionsPercentage > 0;

  // Get analytical comments from AI analysis if available
  const getAnalysis = (metric) => {
    if (analysis_data?.financial_analysis?.[metric]) {
      return analysis_data.financial_analysis[metric];
    }
    return null;
  };

  // Sentiment icon and color
  const getSentimentDisplay = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return { icon: faSmile, color: '#22c55e', label: 'Positive' };
      case 'negative':
        return { icon: faFrown, color: '#ef4444', label: 'Negative' };
      default:
        return { icon: faMeh, color: '#f59e0b', label: 'Neutral' };
    }
  };

  const sentimentDisplay = getSentimentDisplay(glassdoorData.sentiment);

  return (
    <>
      {/* Company Description */}
      {analysis_data.description && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h3 className="text-xl font-semibold mb-2">Company Description</h3>
          <p>{analysis_data.description}</p>
        </div>
      )}

      {/* Trading Range Insights */}
      <TradingRangeInsights stock={stock} />

      {/* Company Snapshot */}
      <div className="bg-white rounded-lg shadow p-6 mb-4">
        <SectionHeading icon={faBuilding}>Company Snapshot</SectionHeading>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px' }}>
          {stock.marketCap != null && (
            <SnapshotTile label="Market Cap">{formatMarketCap(stock.marketCap)}</SnapshotTile>
          )}
          {stock.industry && (
            <SnapshotTile label="Industry">{stock.industry}</SnapshotTile>
          )}
          {/* Revenue per employee is a ratio OF the headcount, so it rides as the
              tile's sub-line rather than taking a tile of its own (issue #451). */}
          {(stock.fullTimeEmployees != null || stock.revenuePerEmployee != null) && (
            <SnapshotTile label="Employees">
              {stock.fullTimeEmployees != null ? stock.fullTimeEmployees.toLocaleString() : '—'}
              {stock.revenuePerEmployee != null && (
                <SnapshotSub>
                  <span title={columnTooltip('revenuePerEmployee')}>
                    {columnFormat('revenuePerEmployee', stock.revenuePerEmployee)} revenue / employee
                  </span>
                </SnapshotSub>
              )}
            </SnapshotTile>
          )}
          {stock.averageAnalystRating && (
            <SnapshotTile label="Analyst Consensus" title={columnTooltip('analystRating')}>
              <AnalystRatingText rating={stock.averageAnalystRating} />
            </SnapshotTile>
          )}
          {lastReport.reportedOn && (
            <SnapshotTile label="Last Report" title={columnTooltip('stockChange')}>
              {lastReport.reportedOn}
              <span style={QUIET_STYLE}>
                {' · '}{lastReport.sessions === 0 ? 'today' : `${sessionsLabel(lastReport.sessions, 'trading day')} ago`}
              </span>
              {lastReport.reaction !== null && (
                <SnapshotSub>
                  <span style={{ color: signColor(lastReport.reaction), fontWeight: 600 }}>{formatSignedPercent(lastReport.reaction)}</span> on the day
                </SnapshotSub>
              )}
              {lastReport.since !== null && (
                <SnapshotSub>
                  <span style={{ color: signColor(lastReport.since), fontWeight: 600 }}>{formatSignedPercent(lastReport.since)}</span> since
                </SnapshotSub>
              )}
            </SnapshotTile>
          )}
          {stock.expectationsBar != null && (
            <SnapshotTile label="Expectations Bar" title={columnTooltip('expectationsBar')}>
              <ExpectationsBarMeter
                score={stock.expectationsBar}
                rating={stock.expectationsBarRating}
                coverage={stock.expectationsBarCoverage}
                impliedMove={stock.impliedMovePct}
              />
            </SnapshotTile>
          )}
          {aiCall && (
            <SnapshotTile
              label="AI Assessment"
              title={aiAnalysis.recommendation_explanation || 'AI opinion — see the AI Assessment section below'}
            >
              <AiCallText recommendation={aiCall} />
              {speculation && <SnapshotSub>Speculation {speculation.value}/10</SnapshotSub>}
              {asOf && <SnapshotSub>as of {asOf.label}</SnapshotSub>}
            </SnapshotTile>
          )}
        </div>
      </div>

      {hasAnalysis ? (
        <AiAssessment aiAnalysis={aiAnalysis} asOf={asOf} />
      ) : (
        <p style={{ ...NOTE_STYLE, margin: '0 0 16px' }}>
          No AI analysis is available for this stock yet.
        </p>
      )}

      {/* Market Size and Growth */}
      {analysis_data.market_size_and_growth && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h3 className="text-xl font-semibold mb-2">Market Size and Growth</h3>
          <p>{analysis_data.market_size_and_growth}</p>
        </div>
      )}

      {/* Financial Metrics Section */}
      <div className="financial-metrics">
        <h3 className="text-xl font-semibold mb-6">Financial Metrics</h3>
        <div className="financial-metrics-grid">
          {/* Growth & Valuation */}
          <div className="metric-group">
            <h4 className="metric-group-title">Growth & Valuation</h4>
            <MetricRow 
              label="Revenue Growth"
              value={stock.revenueGrowth ? (stock.revenueGrowth * 100).toFixed(1) : 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.revenueGrowth}
              isPercentage
              metricName="revenueGrowth"
              analysis={getAnalysis('revenue_growth')}
            />
            <MetricRow 
              label="PEG Ratio"
              value={stock.pegRatio?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.pegRatio}
              analysis={getAnalysis('peg_ratio')}
              metricName="pegRatio"
            />
            <MetricRow 
              label="P/S (TTM)"
              value={stock.priceToSalesTrailing12Months?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.priceToSales}
              analysis={getAnalysis('price_to_sales')}
              metricName="priceToSales"
            />
            <MetricRow 
              label="Enterprise/Revenue"
              value={stock.enterpriseToRevenue?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.enterpriseToRevenue}
              analysis={getAnalysis('enterprise_to_revenue')}
              metricName="enterpriseToRevenue"
            />
            <MetricRow
              label="Enterprise/EBITDA"
              value={stock.enterpriseToEbitda?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.enterpriseToEbitda}
              analysis={getAnalysis('enterprise_to_ebitda')}
              metricName="enterpriseToEbitda"
            />
            <MetricRow
              label="Trailing P/E"
              value={stock.trailingPE?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.trailingPE}
              metricName="trailingPE"
            />
            <MetricRow
              label="Forward P/E"
              value={stock.forwardPE?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.forwardPE}
              metricName="forwardPE"
            />
            <MetricRow
              label="Price to Book"
              value={stock.priceToBook?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.priceToBook}
              metricName="priceToBook"
            />
          </div>

          {/* Profitability & Margins */}
          <div className="metric-group">
            <h4 className="metric-group-title">Profitability & Margins</h4>
            <MetricRow 
              label="Gross Margin"
              value={stock.grossMargins ? (stock.grossMargins * 100).toFixed(1) : 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.grossMargins}
              isPercentage
              analysis={getAnalysis('gross_margin')}
              metricName="grossMargin"
            />
            <MetricRow 
              label="Operating Margin"
              value={stock.operatingMargins ? (stock.operatingMargins * 100).toFixed(1) : 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.operatingMargins}
              isPercentage
              analysis={getAnalysis('operating_margin')}
              metricName="operatingMargin"
            />
            <MetricRow 
              label="EPS (TTM)"
              value={stock.trailingEps?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.trailingEps}
              isCurrency
              analysis={getAnalysis('trailing_eps')}
              metricName="trailingEps"
            />
            <MetricRow
              label="EPS (Forward)"
              value={stock.forwardEps?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.forwardEps}
              isCurrency
              analysis={getAnalysis('forward_eps')}
              metricName="forwardEps"
            />
            <MetricRow
              label="Dividend Yield"
              value={stock.dividendYield ? stock.dividendYield.toFixed(2) : 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.dividendYield}
              isPercentage
              metricName="dividendYield"
            />
            <MetricRow
              label="Book Value"
              value={stock.bookValue?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.bookValue}
              isCurrency
            />
          </div>

          {/* Liquidity & Risk */}
          <div className="metric-group">
            <h4 className="metric-group-title">Liquidity & Risk</h4>
            <MetricRow 
              label="Quick Ratio"
              value={stock.quickRatio?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.quickRatio}
              analysis={getAnalysis('quick_ratio')}
              metricName="quickRatio"
            />
            <MetricRow 
              label="Current Ratio"
              value={stock.currentRatio?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.currentRatio}
              analysis={getAnalysis('current_ratio')}
              metricName="currentRatio"
            />
            <MetricRow 
              label="Cash per Share"
              value={stock.totalCashPerShare?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.totalCashPerShare}
              isCurrency
              analysis={getAnalysis('cash_per_share')}
              metricName="cashPerShare"
            />
            <MetricRow 
              label="Short % of Float"
              value={stock.shortPercentOfFloat ? (stock.shortPercentOfFloat * 100).toFixed(2) : 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.shortPercentOfFloat}
              isPercentage
              analysis={getAnalysis('short_percent_of_float')}
              metricName="shortPercentOfFloat"
            />
            <MetricRow
              label="Beta"
              value={stock.beta?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.beta}
              analysis={getAnalysis('beta')}
              metricName="beta"
            />
            <MetricRow
              label="Alpha (12M)"
              value={stock.alpha_12m?.toFixed(2) || 'N/A'}
              tooltip={METRIC_DESCRIPTIONS.alpha12m}
              isPercentage
              metricName="alpha12m"
            />
          </div>
        </div>
      </div>

      {/* Competition: the subject and its AI-named competitors side by side (issue #445) */}
      <CompetitionSection stock={stock} competitors={analysis_data.competition?.competitors} />

      {/* Growth Prospects */}
      {analysis_data.growth_prospects && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h3 className="text-xl font-semibold mb-2">Growth Prospects</h3>
          <p>{analysis_data.growth_prospects}</p>
        </div>
      )}

      {/* SWOT Analysis */}
      {analysis_data.swot_analysis && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <h3 className="text-xl font-semibold mb-4">SWOT Analysis</h3>
          <div className="swot-grid">
            <div className="swot-item swot-strengths">
              <h4 className="swot-title">
                <FontAwesomeIcon icon={faCheckCircle} className="swot-icon" />
                Strengths
              </h4>
              <ul className="swot-list">
                {analysis_data.swot_analysis.strengths?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="swot-item swot-weaknesses">
              <h4 className="swot-title">
                <FontAwesomeIcon icon={faExclamationTriangle} className="swot-icon" />
                Weaknesses
              </h4>
              <ul className="swot-list">
                {analysis_data.swot_analysis.weaknesses?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="swot-item swot-opportunities">
              <h4 className="swot-title">
                <FontAwesomeIcon icon={faLightbulb} className="swot-icon" />
                Opportunities
              </h4>
              <ul className="swot-list">
                {analysis_data.swot_analysis.opportunities?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>

            <div className="swot-item swot-threats">
              <h4 className="swot-title">
                <FontAwesomeIcon icon={faShieldAlt} className="swot-icon" />
                Threats
              </h4>
              <ul className="swot-list">
                {analysis_data.swot_analysis.threats?.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Key Developments beside Risk Factors (issue #445) */}
      <DevelopmentsAndRisks analysisData={analysis_data} asOf={asOf} />

      {/* Employee Insights. The metrics (Glassdoor rating, Employee Outlook, Job
          Openings — the last two are table columns) render on their own: they
          used to sit inside the AI-summary branch, so a stock with the numbers
          but no summary yet showed none of them (issue #445). */}
      {(glassdoorData.summary || glassdoorData.loading || hasGlassdoor || hasEmployeeMetrics) && (
        <div className="bg-white rounded-lg shadow p-6 mb-4">
          <SectionHeading icon={faUsers}>Employee Insights</SectionHeading>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 20px', marginBottom: 12, fontSize: '0.875rem' }}>
            {stock.glassdoorOverallRating && (
              <EmployeeMetric label="Rating">{stock.glassdoorOverallRating.toFixed(1)}/5</EmployeeMetric>
            )}
            {stock.glassdoorReviewCount && (
              <EmployeeMetric label="Reviews">{stock.glassdoorReviewCount.toLocaleString()}</EmployeeMetric>
            )}
            {stock.glassdoorCeoName && (
              <EmployeeMetric label="CEO">{stock.glassdoorCeoName.trim()}</EmployeeMetric>
            )}
            {glassdoorData.summary && glassdoorData.sentiment && (
              <EmployeeMetric label="Sentiment">
                <FontAwesomeIcon icon={sentimentDisplay.icon} style={{ color: sentimentDisplay.color, marginRight: 4 }} />
                <span style={{ color: sentimentDisplay.color }}>{sentimentDisplay.label}</span>
              </EmployeeMetric>
            )}
            {stock.outlook != null && (
              <EmployeeMetric label="Business Outlook" title={columnTooltip('outlook')}>
                {(stock.outlook * 100).toFixed(0)}%
                {stock.outlookDelta != null && stock.outlookDelta !== 0 && (
                  <span style={{ fontSize: '0.75rem', marginLeft: 4, color: signColor(stock.outlookDelta) }}>
                    ({stock.outlookDelta > 0 ? '+' : ''}{(stock.outlookDelta * 100).toFixed(0)}%)
                  </span>
                )}
              </EmployeeMetric>
            )}
            {stock.positionsPercentage > 0 && (
              <EmployeeMetric label="Open Positions" title={columnTooltip('positionsPercentage')}>
                {stock.positions != null && `${stock.positions.toLocaleString()} · `}
                {stock.positionsPercentage.toFixed(1)}% of workforce
              </EmployeeMetric>
            )}
          </div>

          {glassdoorData.loading ? (
            <p style={NOTE_STYLE}>Generating employee sentiment analysis...</p>
          ) : glassdoorData.summary ? (
            <div>
              <p className="text-gray-700">{glassdoorData.summary}</p>
              <p style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: 8 }}>Based on employee reviews</p>
            </div>
          ) : hasGlassdoor ? (
            <p style={NOTE_STYLE}>
              Employee insights will be generated when available.
            </p>
          ) : null}
        </div>
      )}

      {/* Web Traffic — an alternative-data demand signal, so it sits with
          Employee Insights at the end rather than up among the fundamentals,
          and well away from the trading-range chart, which is a price surface.
          Renders nothing unless a same-month year-over-year pair exists.

          This replaces a two-stat block that used to sit right here. That one
          put "YoY Change" beside "Current Monthly Visits" as though they
          described one period — but the change is the newest COMPARABLE month
          (Dec 2025 vs Dec 2024) while the visit count is the newest COLLECTED
          month (Jul 2026), and the 2025-03 → 2025-09 coverage hole means those
          are routinely months apart. */}
      <WebTrafficSection stock={stock} />

      {/* Updated At */}
      {hasAnalysis && (
        <div className="ai-disclaimer-text flex items-center gap-2">
          <FontAwesomeIcon icon={faTriangleExclamation} className="ai-disclaimer-icon" />
          <p>Some data generated with help of LLM AI, subject to possible "hallucinations". Please do your own research.{asOf && ` AI analysis written ${asOf.isoDate}.`}</p>
        </div>
      )}
    </>
  );
};

export default AiAnalysisPanel;